// PricingModal.jsx — Popup "Completar con precio de lista" (motor de precios de separadores).
// Elegís producto + forma de entrega + cantidad → calcula en vivo con pricing_calcular.php
// y muestra el desglose. "Usar este precio" devuelve el valor al ítem/escalón.
import React, { useState, useEffect } from "react";
import axios from "axios";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

const fmt = (n) => "$" + (parseFloat(n) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtEnt = (n) => (parseFloat(n) || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 });

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  modal: { background: "#fff", borderRadius: 14, padding: "24px 28px", width: "100%", maxWidth: 440, boxShadow: "0 10px 40px rgba(0,0,0,0.25)", maxHeight: "90vh", overflowY: "auto" },
  title: { margin: 0, fontSize: 17, fontWeight: 700, color: "#9f0059" },
  sub: { margin: "4px 0 18px", fontSize: 12.5, color: "#888" },
  label: { display: "block", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, marginTop: 12 },
  input: { width: "100%", padding: "9px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, outline: "none", color: "#222", boxSizing: "border-box", background: "#fff" },
  desglose: { marginTop: 16, background: "#fdf5fa", border: "1.5px solid #e8d0de", borderRadius: 10, padding: "12px 16px" },
  dRow: { display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#555", marginBottom: 4 },
  precioFinal: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8, paddingTop: 8, borderTop: "1.5px solid #e8d0de" },
  error: { marginTop: 14, padding: "10px 14px", background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 8, fontSize: 12.5, color: "#991b1b" },
  btnRow: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 },
  btnCancel: { padding: "9px 18px", background: "#fff", color: "#888", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnUsar: (enabled) => ({ padding: "9px 20px", background: enabled ? "#9f0059" : "#cbb3c2", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: enabled ? "pointer" : "not-allowed" }),
};

const PricingModal = ({ cantidadInicial, onUsar, onClose }) => {
  const [productos, setProductos] = useState([]);
  const [formas,    setFormas]    = useState([]);
  const [productoId, setProductoId] = useState("");
  const [formaId,    setFormaId]    = useState("");
  const [cantidad,   setCantidad]   = useState(cantidadInicial ? String(cantidadInicial) : "");
  const [resultado,  setResultado]  = useState(null);   // { precio, detalle }
  const [error,      setError]      = useState("");
  const [calculando, setCalculando] = useState(false);

  useEffect(() => {
    axios.post(`${BASE}/pricing_config_listar.php`)
      .then((r) => {
        if (r.data?.success) {
          setProductos(r.data.productos || []);
          setFormas(r.data.formas || []);
        }
      })
      .catch(() => setError("No se pudo cargar la configuración de precios."));
  }, []);

  // Calcular en vivo cuando están los tres valores
  useEffect(() => {
    setResultado(null);
    setError("");
    if (!productoId || !formaId || !cantidad || parseFloat(cantidad) <= 0) return;
    setCalculando(true);
    const t = setTimeout(() => {
      axios.post(`${BASE}/pricing_calcular.php`, new URLSearchParams({
        producto_id: productoId,
        forma_entrega_id: formaId,
        cantidad,
      }))
        .then((r) => {
          if (r.data?.success) setResultado(r.data);
          else setError(r.data?.mensaje || "No se pudo calcular.");
        })
        .catch(() => setError("Error de conexión."))
        .finally(() => setCalculando(false));
    }, 250);
    return () => clearTimeout(t);
  }, [productoId, formaId, cantidad]);

  const det = resultado?.detalle;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={S.title}>⚡ Precio de lista</h3>
        <p style={S.sub}>Elegí el separador, la forma de entrega y la cantidad. El precio se calcula solo.</p>

        <label style={S.label}>Producto (separador) *</label>
        <select style={S.input} value={productoId} onChange={(e) => setProductoId(e.target.value)} autoFocus>
          <option value="">— Elegí un separador —</option>
          {productos.map((p) => (
            <option key={p.id} value={p.id}>{p.nombre} (pallet {fmtEnt(p.cantidad_pallet)})</option>
          ))}
        </select>
        {productos.length === 0 && (
          <div style={{ fontSize: 11.5, color: "#a16207", marginTop: 4 }}>
            No hay separadores cargados. Cargalos en Administración → Calculadora de precios.
          </div>
        )}

        <label style={S.label}>Forma de entrega *</label>
        <select style={S.input} value={formaId} onChange={(e) => setFormaId(e.target.value)}>
          <option value="">— Elegí la entrega —</option>
          {formas.map((f) => (
            <option key={f.id} value={f.id}>{f.nombre}</option>
          ))}
        </select>

        <label style={S.label}>Cantidad *</label>
        <input style={S.input} type="number" min="1" value={cantidad} onChange={(e) => setCantidad(e.target.value)} />

        {/* Desglose */}
        {calculando && <div style={{ marginTop: 14, fontSize: 12.5, color: "#999" }}>Calculando…</div>}
        {error && <div style={S.error}>{error}</div>}
        {resultado && det && (
          <div style={S.desglose}>
            <div style={S.dRow}><span>Precio de lista</span><span>{fmt(det.precio_lista)}</span></div>
            <div style={S.dRow}><span>Cantidad / pallet</span><span>{fmtEnt(cantidad)} / {fmtEnt(det.cantidad_pallet)} ({Math.round(det.porcentaje * 100)}%)</span></div>
            {det.pallet_completo ? (
              <div style={{ ...S.dRow, color: "#166534", fontWeight: 600 }}>
                <span>Pallet completo</span><span>precio de lista, sin recargos</span>
              </div>
            ) : (
              <>
                <div style={S.dRow}><span>Recargo del tramo</span><span>{Math.round(det.recargo * 100)}%</span></div>
                <div style={S.dRow}>
                  <span>{det.tipo_calculo === "DIRECTO_BSAS" ? "Costo logístico (prorrateado)" : "Costo de entrega (prorrateado)"}</span>
                  <span>{fmt(det.costo_aplicado)}</span>
                </div>
              </>
            )}
            <div style={S.precioFinal}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#444" }}>Precio unitario</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: "#9f0059" }}>{fmt(resultado.precio)}</span>
            </div>
          </div>
        )}

        <div style={S.btnRow}>
          <button style={S.btnCancel} onClick={onClose}>Cancelar</button>
          <button
            style={S.btnUsar(!!resultado)}
            disabled={!resultado}
            onClick={() => resultado && onUsar(resultado.precio)}
          >
            Usar este precio
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
